import React, { useState, useCallback, useEffect } from "react";
import "@/App.css";
import { Toaster } from './components/ui/sonner';
import { toast } from 'sonner';
import { MainMenu } from './components/game/MainMenu';
import { GameCanvas } from './components/game/GameCanvas';
import { HUD } from './components/game/HUD';
import { InfoCard } from './components/game/InfoCard';
import { LevelSelector } from './components/game/LevelSelector';
import { GameComplete } from './components/game/GameComplete';
import { levelConfig, portfolioData } from './data/portfolioData';

// Game states
const GAME_STATES = {
  MENU: 'menu',
  PLAYING: 'playing',
  PAUSED: 'paused',
  LEVEL_SELECT: 'level_select',
  COMPLETE: 'complete'
};

function App() {
  const [gameState, setGameState] = useState(GAME_STATES.MENU);
  const [currentLevel, setCurrentLevel] = useState(1);
  const [completedLevels, setCompletedLevels] = useState([]);
  const [coinsCollected, setCoinsCollected] = useState(0);
  const [totalCoins] = useState(15); // Per level
  const [itemsUnlocked, setItemsUnlocked] = useState(0);
  const [totalItemsUnlocked, setTotalItemsUnlocked] = useState(0);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [isPaused, setIsPaused] = useState(false);
  const [showInfoCard, setShowInfoCard] = useState(false);
  const [currentInfoItem, setCurrentInfoItem] = useState(null);
  const [currentInfoTheme, setCurrentInfoTheme] = useState(null);
  const [showLevelSelector, setShowLevelSelector] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Check if mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768 || 'ontouchstart' in window);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const handleStartGame = useCallback(() => {
    setGameState(GAME_STATES.PLAYING);
    setCurrentLevel(1);
    setCoinsCollected(0);
    setItemsUnlocked(0);
    setIsPaused(false);
    toast.success('Welcome to Akhil\'s Adventure! 🎮', {
      description: 'Collect coins and discover portfolio items!'
    });
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.code === 'Escape') {
        if (gameState === GAME_STATES.PLAYING) {
          setIsPaused(prev => !prev);
        }
      }
      if (e.code === 'Space' && gameState === GAME_STATES.MENU) {
        handleStartGame();
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [gameState, handleStartGame]);

  const handleCollectItem = useCallback((item, theme) => {
    setCurrentInfoItem(item);
    setCurrentInfoTheme(theme);
    setShowInfoCard(true);
    setIsPaused(true);
    
    if (theme !== 'npc') {
      setItemsUnlocked(prev => prev + 1);
      setTotalItemsUnlocked(prev => prev + 1);
    }
  }, []);

  const handleCloseInfoCard = useCallback(() => {
    setShowInfoCard(false);
    setCurrentInfoItem(null);
    setCurrentInfoTheme(null);
    setIsPaused(false);
  }, []);

  const handleLevelComplete = useCallback(() => {
    if (!completedLevels.includes(currentLevel)) {
      setCompletedLevels(prev => [...prev, currentLevel]);
    }
    
    if (currentLevel < levelConfig.length) {
      toast.success(`Level ${currentLevel} Complete! 🎉`, {
        description: `Moving to ${levelConfig[currentLevel].name}`
      });
      setCurrentLevel(prev => prev + 1);
      setCoinsCollected(0);
      setItemsUnlocked(0);
    } else {
      // Game complete!
      setGameState(GAME_STATES.COMPLETE);
      toast.success('Congratulations! 🏆', {
        description: 'You\'ve completed Akhil\'s Adventure!'
      });
    }
  }, [currentLevel, completedLevels]);

  const handleSelectLevel = useCallback((levelId) => {
    setCurrentLevel(levelId);
    setCoinsCollected(0);
    setItemsUnlocked(0);
    setShowLevelSelector(false);
    setGameState(GAME_STATES.PLAYING);
    setIsPaused(false);
    toast.info(`Starting ${levelConfig[levelId - 1].name}`, {
      description: levelConfig[levelId - 1].description
    });
  }, []);

  const handleSkipToResume = useCallback(() => {
    // Skip to contact/final info
    window.open('https://andromechanic.github.io', '_blank');
  }, []);

  const handleToggleSound = useCallback(() => {
    setSoundEnabled(prev => !prev);
    toast.info(soundEnabled ? 'Sound Off 🔇' : 'Sound On 🔊');
  }, [soundEnabled]);

  const handleRestart = useCallback(() => {
    setGameState(GAME_STATES.MENU);
    setCurrentLevel(1);
    setCompletedLevels([]);
    setCoinsCollected(0);
    setItemsUnlocked(0);
    setTotalItemsUnlocked(0);
  }, []);

  const handleMainMenu = useCallback(() => {
    setGameState(GAME_STATES.MENU);
  }, []);

  // Render based on game state
  if (gameState === GAME_STATES.MENU) {
    return (
      <>
        <MainMenu 
          onStartGame={handleStartGame}
          onShowLevels={() => setShowLevelSelector(true)}
          onToggleSound={handleToggleSound}
          soundEnabled={soundEnabled}
        />
        {showLevelSelector && (
          <LevelSelector
            currentLevel={currentLevel}
            completedLevels={completedLevels}
            onSelectLevel={handleSelectLevel}
            onClose={() => setShowLevelSelector(false)}
          />
        )}
        <Toaster position="top-center" richColors />
      </>
    );
  }

  if (gameState === GAME_STATES.COMPLETE) {
    return (
      <>
        <GameComplete 
          totalCoins={coinsCollected + (completedLevels.length * totalCoins)}
          itemsUnlocked={totalItemsUnlocked}
          onRestart={handleRestart}
          onMainMenu={handleMainMenu}
        />
        <Toaster position="top-center" richColors />
      </>
    );
  }

  // Playing state
  const level = levelConfig[currentLevel - 1];
  const totalLevelItems = level?.items?.length || 0;

  return (
    <div 
      className="min-h-screen flex flex-col retro-ui"
      style={{ background: level?.gradient || 'radial-gradient(circle at 20% 20%, #6f9bff, #0d1f46)' }}
      data-testid="game-screen"
    >
      {/* HUD */}
      <HUD 
        currentLevel={currentLevel}
        coinsCollected={coinsCollected}
        totalCoins={totalCoins}
        itemsUnlocked={itemsUnlocked}
        totalItems={totalLevelItems}
        isPaused={isPaused}
        onPause={() => setIsPaused(true)}
        onResume={() => setIsPaused(false)}
        onSkipToResume={handleSkipToResume}
      />
      
      {/* Game Canvas Container */}
      <div className="flex-1 flex items-center justify-center p-4 pt-32">
        <div className="max-w-4xl w-full">
          <GameCanvas 
            currentLevel={currentLevel}
            onCollectItem={handleCollectItem}
            onLevelComplete={handleLevelComplete}
            onGameOver={() => {}}
            isPaused={isPaused || showInfoCard}
            isMobile={isMobile}
          />
        </div>
      </div>
      
      {/* Level indicator */}
      <div className="text-center pb-4">
        <button
          onClick={() => setShowLevelSelector(true)}
          className="text-white/60 hover:text-white/90 text-sm transition-colors"
          data-testid="show-levels-btn"
        >
          {level?.name} • Click to change level
        </button>
      </div>
      
      {/* Pause overlay */}
{isPaused && !showInfoCard && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-md z-40 flex items-center justify-center p-2 sm:p-4 max-h-[95vh] overflow-y-auto" data-testid="pause-overlay">
          <div className="bg-white/95 backdrop-blur-xl rounded-2xl sm:rounded-3xl shadow-2xl p-4 sm:p-6 md:p-8 text-center w-full max-w-sm mx-2 sm:mx-auto pause-menu-mobile">
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-slate-800 mb-3 sm:mb-4">⏸️ PAUSED</h2>
            <p className="text-slate-600 mb-4 sm:mb-6 text-sm leading-relaxed">Take a break, explorer! 🌟</p>
            <div className="space-y-2 sm:space-y-3">
              <button
                onClick={() => setIsPaused(false)}
                className="w-full py-3 px-4 sm:px-6 text-sm sm:text-base font-bold bg-gradient-to-r from-sky-500 to-purple-600 hover:from-sky-600 hover:to-purple-700 text-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 min-h-[44px] flex items-center justify-center"
                data-testid="resume-btn"
              >
                ▶️ Resume Game
              </button>
              <button
                onClick={() => setShowLevelSelector(true)}
                className="w-full py-3 px-4 sm:px-6 text-sm sm:text-base font-semibold bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl shadow-md hover:shadow-lg transition-all duration-200 min-h-[44px] flex items-center justify-center"
                data-testid="change-level-btn"
              >
                🎮 Change Level
              </button>
              <button
                onClick={handleMainMenu}
                className="w-full py-3 px-4 sm:px-6 text-sm sm:text-base font-semibold bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl shadow-md hover:shadow-lg transition-all duration-200 min-h-[44px] flex items-center justify-center"
                data-testid="back-to-menu-btn"
              >
                🏠 Main Menu
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Info Card Modal */}
      <InfoCard 
        isOpen={showInfoCard}
        onClose={handleCloseInfoCard}
        item={currentInfoItem}
        theme={currentInfoTheme}
      />
      
      {/* Level Selector Modal */}
      {showLevelSelector && (
        <LevelSelector
          currentLevel={currentLevel}
          completedLevels={completedLevels}
          onSelectLevel={handleSelectLevel}
          onClose={() => setShowLevelSelector(false)}
        />
      )}
      
      <Toaster position="top-center" richColors />
    </div>
  );
}

export default App;
